import { cn } from '@/lib/utils';
import { typography } from '@/styles/typography';
import { useIsDesktop } from '@/lib/hooks/useMediaQuery';
import WarningIcon from '@/icons/Warning.svg?react';

/**
 * Content interface for NoResultsMapDialog
 * Following the UI/content separation pattern
 */
export interface NoResultsMapDialogContent {
  /** Title text displayed in heading large typography */
  title: string;
  /** Main description text (portion before the link) */
  descriptionBeforeLink: string;
  /** Link text that appears inline */
  linkText: string;
  /** URL for the AAA link */
  linkUrl: string;
  /** Text that appears after the link (if any) */
  descriptionAfterLink?: string;
}

/**
 * Props for NoResultsMapDialog component
 */
export interface NoResultsMapDialogProps {
  /** Content for the dialog */
  content: NoResultsMapDialogContent;
  /** Whether the dialog is visible */
  isVisible: boolean;
  /** Additional CSS classes */
  className?: string;
}

/**
 * NoResultsMapDialog - Displays a helpful message when no services match filters
 *
 * This component appears centered on the map when the user's current filters
 * return zero results. It provides guidance to expand search criteria and
 * links to Area Agencies on Aging for additional assistance.
 *
 * @example
 * ```tsx
 * <NoResultsMapDialog
 *   content={{
 *     title: "No results were found",
 *     descriptionBeforeLink: "Try modifying your filters or expanding your search area. This directory does not include all available services—contact your local Area Agencies on Aging (AAA) for information specific to your county at ",
 *     linkText: "https://aging.ohio.gov/about-us/who-we-are/area-agencies-on-aging",
 *     linkUrl: "https://aging.ohio.gov/about-us/who-we-are/area-agencies-on-aging",
 *   }}
 *   isVisible={filteredServices.length === 0}
 * />
 * ```
 */
export function NoResultsMapDialog({
  content,
  isVisible,
  className,
}: NoResultsMapDialogProps) {
  const isDesktop = useIsDesktop();

  if (!isVisible) {
    return null;
  }

  // On tablet/mobile, reduce font sizes by ~4px (24px → 20px)
  const smallerFont = !isDesktop;

  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        'absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2',
        'w-[538px] max-w-[calc(100vw-32px)]',
        'max-h-[calc(100vh-32px)] overflow-y-auto',
        'bg-white border border-[#EAEAEA] rounded-[4px]',
        'p-4 flex flex-col gap-4',
        'z-20',
        className
      )}
      style={{
        boxShadow: '0px 4px 4px 0px #0000001A',
      }}
      data-testid="no-results-map-dialog"
    >
      {/* Warning Icon */}
      <WarningIcon
        className="w-[44px] h-[44px] shrink-0"
        aria-hidden="true"
        data-testid="no-results-warning-icon"
      />

      {/* Text Content Container */}
      <div className="flex flex-col gap-2">
        {/* Title */}
        <h2
          className={cn(typography.headingLarge, 'text-black m-0', smallerFont && 'text-[20px] leading-[28px]')}
          data-testid="no-results-title"
        >
          {content.title}
        </h2>

        {/* Description with inline link */}
        <p
          className={cn(typography.bodyLarge, 'text-black m-0', smallerFont && 'text-[20px] leading-[28px]')}
          data-testid="no-results-description"
        >
          {content.descriptionBeforeLink}
          <a
            href={content.linkUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={cn(
              typography.bodyLargeSemibold,
              'text-[var(--color-primary-brand)] hover:underline',
              smallerFont && 'text-[20px] leading-[28px]'
            )}
            data-testid="no-results-link"
          >
            {content.linkText}
          </a>
          {content.descriptionAfterLink}
        </p>
      </div>
    </div>
  );
}

NoResultsMapDialog.displayName = 'NoResultsMapDialog';
